<template>
   <Teleport to="#modals">
      <dialog ref="modalRef" class="max-w-full [&::backdrop]:bg-backdrop" :class="modalWidth" :data-cy="`${id}-modal`">
         <header class="shrink-0 pb-4">
            <DefaultButton
               class="absolute -top-7 right-0 z-100"
               style-type="outlined"
               :color="Theme.GreyStatic"
               size="sm"
               rounded-edges="md"
               is-icon
               :data-cy="`${id}-modal-close-btn`"
               :aria-label="$t('system.button_exit')"
               @click="closeModalWithEmit('close')"
            >
               <span class="block size-4">
                  <IconClose focusable="false" aria-hidden="true" />
               </span>
            </DefaultButton>

            <div class="flex flex-col items-center px-6 md:px-10">
               <PillTag
                  v-if="pill"
                  class="mx-12 mb-4 inline-flex max-w-sm text-center"
                  :text="pill.text"
                  :theme="pill.theme"
                  :data-cy="`${id}-modal-pill`"
               />
               <div :data-cy="`${id}-modal-title`" class="mt-0">
                  <slot name="title">
                     <h1 v-if="title" class="text-regular grow text-center text-xl text-brand-dark md:text-2xl">
                        {{ title }}
                     </h1>
                  </slot>
               </div>
            </div>
         </header>

         <!-- Body content -->
         <main :data-cy="`${id}-modal-body`" class="grow overflow-y-auto px-2">
            <slot name="default"></slot>
         </main>

         <footer class="footer shrink-0">
            <div class="flex w-full flex-col items-center justify-center gap-6 pt-6 md:flex-row">
               <slot name="action-btns">
                  <!-- Confirm is offered at full width by default -->
                  <DefaultButton
                     :data-cy="`${id}-modal-confirm-btn`"
                     size="sm"
                     class="w-full max-w-[60%]"
                     @click="closeModalWithEmit('confirm')"
                     >{{ $t("system.button_confirm") }}</DefaultButton
                  >
               </slot>
            </div>
         </footer>
      </dialog>
   </Teleport>
</template>

<script setup lang="ts">
import { Theme } from "@/types";
import type { PillTagTheme } from "@/components/ui/PillTag.vue";

const modalRef = ref(null);
const emit = defineEmits(["close", "confirm"]);

export interface ModalProps {
   id: string;
   title?: string;
   width?: "sm" | "md";
   pill?: {
      text: string;
      theme: PillTagTheme;
   };
}

const props = withDefaults(defineProps<ModalProps>(), {
   pill: null,
   width: "sm",
});

// lifecycle
// -----------------------------------------
onMounted(() => {
   modalRef.value.showModal();

   modalRef.value.addEventListener("cancel", () => {
      closeModalWithEmit("close");
   });
});

// computed
// -----------------------------------------
const modalWidth = computed(() => {
   switch (props.width) {
      case "sm":
         return "sm:max-w-sm";
      case "md":
         return "sm:max-w-md";
   }
});

// methods
// -----------------------------------------
function closeModalWithEmit(buttonAction: "confirm" | "close" = "close") {
   closeModal();
   emit(buttonAction);
}

function closeModal() {
   modalRef.value.close();
}

// Allows parent component to trigger closeModal() function
// Handy if the parent component uses custom buttons to close the modal
defineExpose({
   closeModal,
});
</script>

<style scoped lang="scss">
dialog[open] {
   display: flex;
   flex-direction: column;
   opacity: 1;
}

dialog {
   @apply px-3 py-12 rounded-t-lg bg-white;

   max-height: 80dvh;
   position: fixed;
   bottom: 0;
   top: auto;
   width: 100%;
   overflow: hidden;
   transition: opacity 0.5s; // dialog transitioning
   opacity: 0;

   @screen sm {
      @apply -translate-y-1/2 rounded-b-lg px-6;
      bottom: auto;
      top: 50%;
      margin-left: auto;
      margin-right: auto;
   }
}

@starting-style {
   dialog[open] {
      opacity: 0;
   }
}
</style>
